import { useCallback } from 'react';
import { MessageBubble, MessageBubbleProps } from '../../Message/MessageBubble';
import { ConvexMessage } from '../../../types/message';
import '../../../Styles/Layout/Views/MessagesList.css';
import { MessagesSkeleton } from '../Skeletons';
import { useAuth } from '../../../contexts/AuthContext';
import { Mode } from '../../../types/mode';
import { useChatContext } from '../../../contexts/AppDataContext';

interface MessagesListProps {
  renderDefaultDisplay: () => React.ReactElement;
  chatId?: string | null;
  handleResourceClick: (resourceTitle: string, messageId: string) => void;
  memoizedEditActions: MessageBubbleProps['editActions'];
  memoizedMessageActions: {
    RegenerateMessage: (messageId: string, mode: Mode) => Promise<void>;
  };
  mode: Mode;
  getRenderFunctions: () => MessageBubbleProps['renderFunctions'];
  messagesContainerRef: React.RefObject<HTMLDivElement>;
  filteredMessages: ConvexMessage[];
  Spacer: () => React.ReactElement | null;
  /** Shared chats: single branch, no edit or regenerate. */
  readOnly?: boolean;
}

const MessagesList = ({
  renderDefaultDisplay,
  chatId,
  handleResourceClick,
  memoizedEditActions,
  memoizedMessageActions,
  mode,
  getRenderFunctions,
  messagesContainerRef,
  filteredMessages,
  Spacer,
  readOnly = false
}: MessagesListProps) => {
  const auth = useAuth();
  const { 
    setActiveBranchForPair, 
    branchesMap: contextBranchesMap, 
    setGlobalBranchCount,
    isLoadingMessages 
  } = useChatContext();

  const renderMessage = useCallback((
    message: ConvexMessage, 
  ) => {
    const branchesKey = `${message.messagePairId || ''}-${message.isAi}`;
    const branches = readOnly ? [] : contextBranchesMap.get(branchesKey) || [];
    
    const content = message.content && typeof message.content === 'string' 
                    ? message.content 
                    : "";

    const updatedMessage = {
      ...message,
      content: content,
    };

    const handleBranchChangeForBubble = (branchId: number) => {
      if (!message.messagePairId) return;
      const pairId = message.messagePairId;
      // Update active branch for this message pair
      setActiveBranchForPair(pairId, branchId);
      // Also update global branch count so overall context matches
      setGlobalBranchCount(branchId);
    };

    const reactKey = message._id || `${branchesKey}-${message.timestamp}`;

    return (
      <MessageBubble
        key={reactKey}
        message={updatedMessage}
        handleResourceClick={handleResourceClick}
        editActions={memoizedEditActions}
        messageActions={memoizedMessageActions}
        currentMode={mode}
        renderFunctions={getRenderFunctions()}
        handleBranchChange={handleBranchChangeForBubble}
        branches={branches}
        readOnly={readOnly}
      />
    );
  }, [
    handleResourceClick,
    memoizedEditActions,
    memoizedMessageActions,
    mode,
    getRenderFunctions,
    setActiveBranchForPair,
    contextBranchesMap,
    setGlobalBranchCount,
    readOnly,
  ]);

  // Consolidate loading conditions to prevent jumping between states
  const shouldShowSkeleton = chatId && chatId !== 'new' && (
    auth.isLoading || 
    auth.isDataLoading ||
    (isLoadingMessages && filteredMessages.length === 0)
  );

  return (
    <>
      {shouldShowSkeleton ? (
        <div className="messages-wrapper" style={{ overscrollBehavior: 'contain' }} ref={messagesContainerRef}>
          <MessagesSkeleton />
        </div>
      ) : (
        <>
          {filteredMessages.length === 0 ? (
            renderDefaultDisplay()
          ) : (
            <div className="messages-wrapper" style={{ overscrollBehavior: 'contain' }} ref={messagesContainerRef}>
              <div className="messages-content">
                {filteredMessages.map(renderMessage)}
                <Spacer />
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default MessagesList;
